import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Smartphone, X, Download, Check } from "lucide-react";
import { useInstallPrompt } from "@/lib/install";

const STEPS = {
  android: [
    "Tap Install below (or Chrome's ⋮ menu → Install app).",
    "Accept the Chrome popup.",
    "Swipe up to open your app drawer and find \"Pick Em\".",
    "Long-press the icon and drag it onto your home screen.",
  ],
  ios: [
    "Open this site in Safari (Chrome on iPhone can't install apps).",
    "Tap the Share button at the bottom.",
    "Scroll down and tap \"Add to Home Screen\".",
    "Tap Add — the icon lands on your home screen.",
  ],
  desktop: [
    "Click Install below, or the install icon in Chrome's address bar.",
    "Confirm in the popup.",
    "Pick Em opens in its own window and lands in your app list.",
  ],
};

export default function InstallButton() {
  const { available, installed, install, platform } = useInstallPrompt();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);

  if (installed) return null;

  const onInstall = async () => {
    const outcome = await install();
    if (outcome === "accepted") setDone(true);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-white/60 hover:text-[#E31837] transition-colors duration-200"
        data-testid="nav-install-button"
      >
        <Smartphone className="w-4 h-4" /> <span className="hidden sm:inline">Install</span>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="install-modal-overlay">
          <div className="bg-[#141414] border border-white/15 w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="install-modal">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors duration-200" aria-label="Close" data-testid="install-modal-close">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="" className="w-12 h-12" />
              <div>
                <div className="font-['Bebas_Neue'] text-2xl tracking-wide leading-none">Install Pick Em</div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1">
                  {platform === "ios" ? "iPhone / iPad" : platform === "android" ? "Android" : "Desktop"}
                </p>
              </div>
            </div>

            <p className="text-xs text-white/50 mt-4 leading-relaxed">
              This adds a full-screen app icon to your device. There's no App Store download — nothing to wait for.
            </p>

            <ol className="mt-4 space-y-2.5" data-testid="install-steps">
              {STEPS[platform].map((s, i) => (
                <li key={s} className="flex gap-3 text-sm text-white/80">
                  <span className="shrink-0 w-5 h-5 bg-[#E31837] text-white text-[11px] font-semibold flex items-center justify-center">{i + 1}</span>
                  <span className="leading-snug">{s}</span>
                </li>
              ))}
            </ol>

            {done && (
              <p className="mt-4 flex items-start gap-2 text-xs text-[#00C176] leading-relaxed" data-testid="install-modal-done">
                <Check className="w-4 h-4 shrink-0" /> Installed. Check your app drawer for "Pick Em" and drag it to your home screen.
              </p>
            )}

            {available && !done && (
              <button onClick={onInstall} className="mt-5 w-full flex items-center justify-center gap-2 bg-[#E31837] hover:bg-[#C8102E] px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-200" data-testid="install-modal-button">
                <Download className="w-4 h-4" /> Install now
              </button>
            )}
            {!available && !done && (
              <p className="mt-5 text-[11px] uppercase tracking-wider text-white/35" data-testid="install-modal-manual">
                Follow the steps above using your browser menu.
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
